/**
 * The seller's name, as the rest of the app needs it.
 *
 * `USERS.firstName` is nullable and phone-only signups arrive without one, yet it is
 * the seller's public display name and the name partners such as Vendoo read back.
 * The places that ask for it (login's more-info step, the sell wizard, the missing-name
 * notice) share these two rules so they agree on who still needs asking.
 */

/** Whether a profile carries a usable first name — whitespace does not count. */
export const hasFirstName = (profile?: { firstName?: string | null } | null): boolean =>
  Boolean(profile?.firstName?.trim());

/**
 * Whether a page should ask the signed-in user for a name. Not before the profile
 * has loaded — flashing the ask at someone who has a name is worse than a moment's
 * delay.
 */
export const needsNameCapture = (
  loggedIn: boolean,
  profile: { firstName?: string | null } | null | undefined
): boolean => loggedIn && Boolean(profile) && !hasFirstName(profile);

/**
 * Split a single "Name" field into the two columns. The first word is the first name;
 * everything after it is the last name, which stays optional — plenty of people give
 * one name, and a missing last name must never block anyone.
 */
export const splitFullName = (fullName: string): { firstName: string; lastName?: string } => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.length > 1 ? parts.slice(1).join(" ") : undefined,
  };
};
